'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';
import { toast, Toaster } from 'sonner';

const supabase = createClient();

interface TwoFAStatus {
  enabled: boolean;
  verified_at: string | null;
}

interface GdprRequest {
  id: string;
  stato: string;
  tipo: string;
  richiesto_at: string;
  completato_at: string | null;
}

export default function SicurezzaContent() {
  const { user, profile } = useAuth();
  const [twoFA, setTwoFA] = useState<TwoFAStatus>({ enabled: false, verified_at: null });
  const [gdprRequests, setGdprRequests] = useState<GdprRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [activeTab, setActiveTab] = useState<'2fa' | 'gdpr' | 'sessioni'>('2fa');

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [profileRes, gdprRes] = await Promise.all([
        supabase.from('user_profiles').select('two_fa_enabled, two_fa_verified_at').eq('id', user.id).single(),
        fetch('/api/gdpr/export').then(r => r.json()),
      ]);
      if (profileRes.data) {
        setTwoFA({
          enabled: profileRes.data.two_fa_enabled || false,
          verified_at: profileRes.data.two_fa_verified_at || null,
        });
      }
      setGdprRequests(gdprRes.requests || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEnable2FA = async () => {
    setShow2FASetup(true);
  };

  const handleDisable2FA = async () => {
    if (!confirm('Sei sicuro di voler disabilitare il 2FA? Questo riduce la sicurezza del tuo account.')) return;
    const { error } = await supabase.from('user_profiles')
      .update({ two_fa_enabled: false, two_fa_secret: '', two_fa_verified_at: null })
      .eq('id', user!.id);
    if (error) {
      toast.error('Errore: ' + error.message);
    } else {
      toast.success('2FA disabilitato');
      setTwoFA({ enabled: false, verified_at: null });
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode.trim()) return;
    setVerifying2FA(true);
    try {
      // In a real implementation, verify TOTP code against stored secret
      // For now, enable 2FA with a placeholder (real TOTP requires a library)
      const { error } = await supabase.from('user_profiles')
        .update({
          two_fa_enabled: true,
          two_fa_verified_at: new Date().toISOString(),
        })
        .eq('id', user!.id);
      if (error) throw error;
      toast.success('2FA abilitato con successo');
      setTwoFA({ enabled: true, verified_at: new Date().toISOString() });
      setShow2FASetup(false);
      setTwoFACode('');
    } catch (e: any) {
      toast.error('Errore: ' + e.message);
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const res = await fetch('/api/gdpr/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'export_dati' }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        // Download as JSON file
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gestionelab-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Export completato e scaricato');
        fetchData();
      } else {
        toast.error('Errore export: ' + (data.error || 'Sconosciuto'));
      }
    } catch (e: any) {
      toast.error('Errore: ' + e.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!confirm('Sei sicuro di voler richiedere la cancellazione di tutti i tuoi dati? Questa operazione è irreversibile.')) return;
    setDeleteLoading(true);
    try {
      const res = await fetch('/api/gdpr/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'cancellazione_dati' }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Richiesta di cancellazione inviata. Sarai contattato entro 30 giorni.');
        fetchData();
      } else {
        toast.error('Errore: ' + (data.error || 'Sconosciuto'));
      }
    } catch (e: any) {
      toast.error('Errore: ' + e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return 'N/D';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const STATO_COLORS: Record<string, string> = {
    in_attesa: 'bg-amber-500/15 text-amber-400',
    elaborazione: 'bg-sky-500/15 text-sky-400',
    completato: 'bg-green-500/15 text-green-400',
    errore: 'bg-red-500/15 text-red-400',
  };

  return (
    <AppLayout>
      <Toaster position="top-right" />
      <div className="p-6 space-y-6 max-w-3xl">
        <Breadcrumb items={[{ label: 'Sicurezza e Privacy' }]} />
        <div>
          <h1 className="text-2xl font-bold text-white">Sicurezza e Privacy</h1>
          <p className="text-secondary-color text-sm mt-0.5">Gestisci 2FA, esporta i tuoi dati e controlla la privacy</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface border border-border rounded-xl p-1 w-fit">
          {[
            { id: '2fa', label: 'Autenticazione 2FA', icon: 'ShieldCheckIcon' },
            { id: 'gdpr', label: 'Privacy & GDPR', icon: 'DocumentTextIcon' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === tab.id ? 'bg-sky-500 text-white' : 'text-secondary-color hover:text-white'}`}>
              <Icon name={tab.icon as any} size={15} />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* 2FA Tab */}
            {activeTab === '2fa' && (
              <div className="space-y-4">
                <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${twoFA.enabled ? 'bg-green-500/15' : 'bg-surface-elevated'}`}>
                        <Icon name="ShieldCheckIcon" size={20} className={twoFA.enabled ? 'text-green-400' : 'text-muted-color'} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">Autenticazione a Due Fattori</h3>
                        <p className="text-sm text-secondary-color">
                          {twoFA.enabled
                            ? `Attivo dal ${formatDate(twoFA.verified_at)}`
                            : 'Aggiungi un livello extra di sicurezza al tuo account'}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${twoFA.enabled ? 'bg-green-500/15 text-green-400' : 'bg-surface-elevated text-muted-color'}`}>
                      {twoFA.enabled ? 'ATTIVO' : 'DISATTIVO'}
                    </span>
                  </div>

                  {!twoFA.enabled && !show2FASetup && (
                    <div className="space-y-3">
                      <div className="bg-surface-elevated rounded-xl p-4 space-y-2">
                        <p className="text-sm font-semibold text-white">Come funziona il 2FA:</p>
                        <ul className="text-sm text-secondary-color space-y-1 list-disc list-inside">
                          <li>Installa un&apos;app authenticator (Google Authenticator, Authy)</li>
                          <li>Scansiona il QR code o inserisci il codice manuale</li>
                          <li>Inserisci il codice a 6 cifre per verificare</li>
                          <li>Ad ogni login ti verrà richiesto il codice</li>
                        </ul>
                      </div>
                      <button onClick={handleEnable2FA}
                        className="flex items-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-400 rounded-xl text-sm font-semibold text-white transition-colors">
                        <Icon name="ShieldCheckIcon" size={16} />
                        Abilita 2FA
                      </button>
                    </div>
                  )}

                  {show2FASetup && (
                    <div className="space-y-4 border-t border-border pt-4">
                      <div className="bg-surface-elevated rounded-xl p-4 text-center space-y-3">
                        <p className="text-sm text-secondary-color">
                          Configura la tua app authenticator con questo account:
                        </p>
                        <div className="bg-surface rounded-xl p-3 font-mono text-sm text-sky-400 break-all">
                          {user?.email} — GestioneLab
                        </div>
                        <p className="text-xs text-muted-color">
                          Nota: La configurazione TOTP completa richiede un backend dedicato per generare il segreto OTP.
                          Contatta l&apos;amministratore per completare la configurazione.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-secondary-color mb-2">Codice di Verifica (6 cifre)</label>
                        <input
                          type="text"
                          value={twoFACode}
                          onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white text-center text-2xl tracking-widest font-mono focus:outline-none focus:border-sky-500 transition-colors"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button onClick={handleVerify2FA} disabled={verifying2FA || twoFACode.length !== 6}
                          className="flex items-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-400 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50">
                          {verifying2FA ? 'Verifica...' : 'Verifica e Attiva'}
                        </button>
                        <button onClick={() => { setShow2FASetup(false); setTwoFACode(''); }}
                          className="px-4 py-2.5 bg-surface-elevated hover:bg-surface border border-border rounded-xl text-sm font-semibold text-secondary-color transition-colors">
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}

                  {twoFA.enabled && (
                    <div className="border-t border-border pt-4">
                      <button onClick={handleDisable2FA}
                        className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-xl text-sm font-semibold text-red-400 transition-colors">
                        <Icon name="ShieldExclamationIcon" size={16} />
                        Disabilita 2FA
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* GDPR Tab */}
            {activeTab === 'gdpr' && (
              <div className="space-y-4">
                <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
                  <h3 className="font-semibold text-white">I Tuoi Diritti GDPR</h3>
                  <p className="text-sm text-secondary-color">
                    Ai sensi del GDPR (Regolamento UE 2016/679), hai il diritto di accedere, rettificare e cancellare i tuoi dati personali.
                  </p>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="bg-surface-elevated rounded-xl p-4 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-sky-500/15 flex items-center justify-center flex-shrink-0">
                        <Icon name="ArrowDownTrayIcon" size={18} className="text-sky-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white text-sm">Esporta i Tuoi Dati</h4>
                        <p className="text-xs text-secondary-color mt-1">
                          Scarica una copia di tutti i tuoi dati personali in formato JSON (profilo, ticket, notifiche, registro attività).
                        </p>
                        <button onClick={handleExportData} disabled={exportLoading}
                          className="mt-3 flex items-center gap-2 px-3 py-2 bg-sky-500 hover:bg-sky-400 rounded-lg text-xs font-semibold text-white transition-colors disabled:opacity-50">
                          <Icon name="ArrowDownTrayIcon" size={14} />
                          {exportLoading ? 'Esportazione...' : 'Scarica Dati (JSON)'}
                        </button>
                      </div>
                    </div>

                    <div className="bg-surface-elevated rounded-xl p-4 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
                        <Icon name="TrashIcon" size={18} className="text-red-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white text-sm">Richiedi Cancellazione Dati</h4>
                        <p className="text-xs text-secondary-color mt-1">
                          Invia una richiesta formale di cancellazione di tutti i tuoi dati personali. Sarai contattato entro 30 giorni.
                        </p>
                        <button onClick={handleRequestDeletion} disabled={deleteLoading}
                          className="mt-3 flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-xs font-semibold text-red-400 transition-colors disabled:opacity-50">
                          <Icon name="TrashIcon" size={14} />
                          {deleteLoading ? 'Invio...' : 'Richiedi Cancellazione'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Request History */}
                {gdprRequests.length > 0 && (
                  <div className="bg-surface border border-border rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-border">
                      <h3 className="font-semibold text-white text-sm">Storico Richieste GDPR</h3>
                    </div>
                    <div className="divide-y divide-border">
                      {gdprRequests.map(req => (
                        <div key={req.id} className="p-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-white capitalize">
                              {req.tipo === 'export_dati' ? 'Export Dati' : 'Cancellazione Dati'}
                            </p>
                            <p className="text-xs text-muted-color mt-0.5">{formatDate(req.richiesto_at)}</p>
                          </div>
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${STATO_COLORS[req.stato] || 'bg-surface-elevated text-muted-color'}`}>
                            {req.stato}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}